import { Badge } from "../../components/Badge";
import { t } from "../../i18n/t";
import type { Support } from "../../api/types";

/**
 * §B3.2's entity toggles, built from `get_migration_capabilities`, redrawn as Scope.html's two-column
 * checkbox grid (name + support pill per entity). Presentational: it renders what it is given and calls
 * `onToggle`.
 *
 * Two rules from §B3.2, both of which are easy to get subtly wrong:
 *
 * * the reason string is rendered **verbatim** — the connector wrote it and it names the actual limitation;
 *   a client-side paraphrase is how "Xero does not expose filed returns through its API" becomes "not
 *   supported" — so it stays, as the muted line under the entity name the design does not draw;
 * * a `NONE` entity's checkbox is **disabled**, not hidden. An operator needs to see that VAT returns exist and
 *   are not coming, which is the same information §A8.10's `not_migrated` sentence carries on the scorecard.
 */

export type CapabilityEntry = { support: Support; reason: string | null };

type Row = { entity: string; support: Support; reason: string | null };

/** §6's tones for the support pill: Full → pass, Partial → warn, Not supported → the grey missing tone. */
const TONE: Readonly<Record<Support, "pass" | "warn" | "neutral">> = {
  FULL: "pass",
  PARTIAL: "warn",
  NONE: "neutral",
};

export function EntityToggles({
  entries,
  selected,
  onToggle,
  disabled = false,
}: {
  entries: Record<string, CapabilityEntry>;
  selected: readonly string[];
  onToggle: (entity: string, include: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  const rows: Row[] = Object.entries(entries)
    .map(([entity, entry]) => ({ entity, support: entry.support, reason: entry.reason }))
    .sort((a, b) => a.entity.localeCompare(b.entity));

  return (
    <div className="entgrid" role="group" aria-label={t("scope.entities_caption")}>
      {rows.map((row) => (
        <label key={row.entity} className="chk chk--entity">
          <input
            type="checkbox"
            aria-label={t("scope.entity_include", { entity: row.entity })}
            checked={selected.includes(row.entity)}
            disabled={disabled || row.support === "NONE"}
            onChange={(event) => onToggle(row.entity, event.target.checked)}
          />
          <span className="chk__main">
            <span className="chk__label">{row.entity}</span>
            {row.reason === null ? null : <span className="chk__sub">{row.reason}</span>}
          </span>
          <Badge tone={TONE[row.support] ?? "neutral"}>{t(`support.${row.support}` as "support.FULL")}</Badge>
        </label>
      ))}
    </div>
  );
}
